import { useState } from 'react'
import {
  accessState,
  accessText,
  checkAccess,
  linkByEmail,
  revenueCatUrl,
  type AmbassadorAccess,
} from './repository/ambassadorAccess'

/**
 * The ambassador's Skida Premium, on the record — 27 Oct 2026.
 *
 * What Lara needs to grant a year of Premium: the app account the email
 * belongs to, a link straight to that customer in RevenueCat, and when the
 * current grant ends. "Check again" asks RevenueCat at once, so a grant just
 * made shows here without waiting for the morning pass. When the email finds
 * no account — they signed up with another address, or with Apple's private
 * relay — the uid can be typed instead.
 */
export function AmbassadorPremium({
  personId,
  email,
  access,
}: {
  personId: string
  email: string
  access: AmbassadorAccess | undefined
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uid, setUid] = useState('')

  const run = async (f: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await f()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const st = accessState(access)
  const url = access ? revenueCatUrl(access) : null
  const color =
    st === 'active'
      ? 'var(--color-success-text)'
      : st === 'expiring'
        ? 'var(--color-warning-text)'
        : st === 'none'
          ? 'var(--color-error-text)'
          : 'var(--color-text-muted)'

  if (!personId) return null

  return (
    <section className="recform__group">
      <h4 className="recform__legend">Skida Premium</h4>
      <p style={{ margin: 0, fontWeight: 600, color }}>{accessText(access)}</p>
      {access?.uid && (
        <p className="micro muted" style={{ margin: 0 }}>
          App account <code>{access.uid}</code>
          {access.manual ? ' · typed by hand' : ` · found by ${access.email}`}
          {access.uids.length > 1 && ` · the email matches ${access.uids.length} accounts; this is the one with access lasting longest`}
        </p>
      )}
      {access?.error && <p className="recform__error">{access.error}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {url && (
          <a className="btn btn--primary btn--tiny" href={url} target="_blank" rel="noreferrer">
            Open in RevenueCat
          </a>
        )}
        {access?.uid && (
          // In case the link's address is ever wrong: RevenueCat's customer
          // search takes the uid as it is.
          <button className="btn btn--ghost btn--tiny" onClick={() => void navigator.clipboard.writeText(access.uid!)}>
            Copy uid
          </button>
        )}
        <button
          className="btn btn--ghost btn--tiny"
          disabled={busy || (!access?.uid && !email.trim())}
          onClick={() =>
            void run(() =>
              access?.uid && access.manual
                ? checkAccess(personId, access.email, access.uids, true)
                : linkByEmail(personId, email),
            )
          }
        >
          {busy ? 'Checking…' : 'Check again'}
        </button>
        {access?.pending && <span className="micro muted">Asking RevenueCat — within two minutes</span>}
        {access?.checkedAt && (
          <span className="micro muted">Checked {access.checkedAt.toLocaleString('nb-NO')}</span>
        )}
      </div>
      <p className="micro muted" style={{ margin: 0 }}>
        Grants are a year at a time: in RevenueCat, open the customer and grant Premium for one year, then
        press Check again. The status turns amber a month before a grant ends.
      </p>
      {(st === 'no-account' || access?.manual) && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="input"
            placeholder="App uid, if the email does not find them"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
          />
          <button
            className="btn btn--ghost btn--tiny"
            disabled={busy || !uid.trim()}
            onClick={() => void run(() => checkAccess(personId, email, [uid.trim()], true))}
          >
            Link
          </button>
        </div>
      )}
      {error && <p className="recform__error">{error}</p>}
    </section>
  )
}
